import {
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
  UserCredential,
} from 'firebase/auth';
import { auth, getSamlProvider, isFirebaseInitialized } from '../config/firebase';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';

function userFacingMicrosoftError(error: string): string {
  const lower = error.toLowerCase();
  if (lower.includes('cancelled') || lower.includes('popup-closed-by-user')) {
    return 'Sign-in was cancelled';
  }
  if (lower.includes('timed out') || lower.includes('timeout')) {
    return 'Sign-in timed out. Complete Microsoft sign-in in your browser, then try again.';
  }
  if (lower.includes('popup-blocked')) {
    return 'Popup was blocked. Allow popups and try again.';
  }
  if (
    lower.includes('redirect auth failed') ||
    lower.includes('getredirectresult') ||
    lower.includes('invalid response from sign-in dialog')
  ) {
    return 'Microsoft sign-in failed. Please try again.';
  }
  if (lower.includes('could not reach courtyard backend')) {
    return 'Could not reach Courtyard. Please try again.';
  }
  if (error.length > 100) {
    return 'Microsoft sign-in failed. Please try again.';
  }
  return error;
}

/** True when running inside Word/Office (task pane or dialog). */
function isOfficeHost(): boolean {
  try {
    return (
      typeof Office !== 'undefined' &&
      typeof Office.context !== 'undefined' &&
      typeof Office.context.ui?.displayDialogAsync === 'function'
    );
  } catch {
    return false;
  }
}

export interface FirebaseAuthResult {
  success: boolean;
  user?: User;
  error?: string;
  idToken?: string;
  accessToken?: string;
  /** User fields returned from Office dialog when Firebase User is unavailable in task pane. */
  dialogUser?: FirebaseUserInfo;
}

type DialogAuthPayload =
  | {
      success: true;
      idToken: string;
      email: string;
      name: string;
      uid: string;
      emailVerified: boolean;
    }
  | { success: false; error: string };

export interface FirebaseUserInfo {
  email: string;
  name: string;
  displayName: string;
  uid: string;
  emailVerified: boolean;
}

function canUseSystemBrowserPopup(): boolean {
  try {
    if (typeof Office.context.ui.openBrowserWindow !== 'function') {
      return false;
    }
    const requirements = Office.context.requirements;
    if (requirements?.isSetSupported) {
      return requirements.isSetSupported('OpenBrowserWindowApi', '1.1');
    }
    return true;
  } catch {
    return false;
  }
}

type WordBrowserSessionPoll = {
  status: string;
  id_token?: string;
  email?: string;
  name?: string;
  uid?: string;
  email_verified?: boolean;
};

async function pollWordBrowserSession(sessionId: string): Promise<FirebaseAuthResult> {
  const deadline = Date.now() + 5 * 60 * 1000;
  while (Date.now() < deadline) {
    const response = await fetch(`${resolveApiBaseUrl()}/auth/word-browser-session/${sessionId}`);
    if (!response.ok) {
      return {
        success: false,
        error: userFacingMicrosoftError(`Could not reach Courtyard backend (${response.status}).`),
      };
    }
    const payload = (await response.json()) as {
      success?: boolean;
      data?: WordBrowserSessionPoll;
    };
    const data = payload.data;
    if (data?.status === 'complete' && data.id_token) {
      return {
        success: true,
        idToken: data.id_token,
        dialogUser: {
          email: data.email || '',
          name: data.name || '',
          displayName: data.name || '',
          uid: data.uid || '',
          emailVerified: Boolean(data.email_verified),
        },
      };
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  return {
    success: false,
    error: userFacingMicrosoftError(
      'Timed out waiting for browser sign-in. Complete Microsoft sign-in in your browser, then try again.'
    ),
  };
}

/** Firebase signInWithPopup in the system browser; WebView2 blocks popup in Word. */
function signInWithMicrosoftViaSystemBrowser(): Promise<FirebaseAuthResult> {
  const sessionId = crypto.randomUUID();
  const browserUrl = `${window.location.origin}/auth-browser.html?session=${encodeURIComponent(sessionId)}&v=${Date.now()}`;

  try {
    Office.context.ui.openBrowserWindow!(browserUrl);
  } catch (error) {
    return Promise.resolve({
      success: false,
      error: userFacingMicrosoftError(
        error instanceof Error ? error.message : 'Could not open the system browser for sign-in'
      ),
    });
  }

  return pollWordBrowserSession(sessionId);
}

/**
 * Fallback: Office dialog + signInWithRedirect (Mac; Word on the web without openBrowserWindow).
 */
function signInWithMicrosoftViaOfficeDialog(): Promise<FirebaseAuthResult> {
  return new Promise((resolve) => {
    // Bust Office WebView cache so auth-dialog picks up the latest hashed JS bundle.
    const dialogUrl = `${window.location.origin}/auth-dialog.html?v=${Date.now()}`;

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 30, displayInIframe: false },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          resolve({
            success: false,
            error: userFacingMicrosoftError(
              asyncResult.error?.message || 'Failed to open Microsoft sign-in dialog'
            ),
          });
          return;
        }

        const dialog = asyncResult.value;
        let settled = false;
        const finish = (result: FirebaseAuthResult) => {
          if (settled) {
            return;
          }
          settled = true;
          try {
            dialog.close();
          } catch {
            /* dialog may already be closed */
          }
          resolve(result);
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          if (!('message' in arg) || !arg.message) {
            return;
          }
          try {
            const payload = JSON.parse(arg.message) as DialogAuthPayload;
            if (payload.success) {
              finish({
                success: true,
                idToken: payload.idToken,
                dialogUser: {
                  email: payload.email,
                  name: payload.name,
                  displayName: payload.name,
                  uid: payload.uid,
                  emailVerified: payload.emailVerified,
                },
              });
              return;
            }
            finish({
              success: false,
              error: userFacingMicrosoftError(payload.error || 'Microsoft sign-in failed'),
            });
          } catch {
            finish({
              success: false,
              error: userFacingMicrosoftError('Invalid response from sign-in dialog'),
            });
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          // 12006 = dialog closed by user
          if ('error' in arg && arg.error === 12006) {
            finish({ success: false, error: userFacingMicrosoftError('Sign-in was cancelled') });
          }
        });
      }
    );
  });
}

/**
 * Microsoft SSO via Firebase SAML (GCIP).
 * System browser popup in Word desktop; Office dialog fallback; popup in browser dev.
 */
export const signInWithMicrosoft = async (): Promise<FirebaseAuthResult> => {
  if (!isFirebaseInitialized() || !auth) {
    return {
      success: false,
      error: 'Firebase is not initialized. Please check your configuration.',
    };
  }

  const samlProvider = getSamlProvider();
  if (!samlProvider) {
    return {
      success: false,
      error:
        'Microsoft SSO is not configured. Set FIREBASE_SAML_PROVIDER_ID (same value as dashboard VITE_FIREBASE_SAML_PROVIDER_ID).',
    };
  }

  if (isOfficeHost()) {
    if (canUseSystemBrowserPopup()) {
      return signInWithMicrosoftViaSystemBrowser();
    }
    return signInWithMicrosoftViaOfficeDialog();
  }

  try {
    const result: UserCredential = await signInWithPopup(auth, samlProvider);
    const idToken = await result.user.getIdToken();
    return {
      success: true,
      user: result.user,
      idToken,
      accessToken: undefined,
    };
  } catch (error) {
    console.error('Microsoft SAML authentication error:', error);

    let errorMessage = 'Authentication failed';
    if (error instanceof Error) {
      if (error.message.includes('auth/popup-closed-by-user')) {
        errorMessage = 'Sign-in was cancelled';
      } else if (error.message.includes('auth/popup-blocked')) {
        errorMessage = 'Popup was blocked — allow popups for this add-in and try again';
      } else {
        errorMessage = error.message;
      }
    }

    return {
      success: false,
      error: userFacingMicrosoftError(errorMessage),
    };
  }
};

/**
 * Sign in with email and password using Firebase Auth
 */
export const signInWithFirebaseEmail = async (
  email: string,
  password: string
): Promise<FirebaseAuthResult> => {
  if (!isFirebaseInitialized() || !auth) {
    return {
      success: false,
      error: 'Firebase is not initialized. Please check your configuration.',
    };
  }

  try {
    console.log('🔥 Starting Firebase email/password authentication...');
    console.log('📧 Email:', email);

    const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);

    console.log('✅ Firebase email/password authentication successful');
    console.log('👤 User email:', result.user.email);
    console.log('👤 Email verified:', result.user.emailVerified);

    // Get Firebase ID token
    const idToken = await result.user.getIdToken();
    console.log('🔑 Firebase ID token obtained:', idToken ? `${idToken.substring(0, 20)}...` : 'null');

    return {
      success: true,
      user: result.user,
      idToken: idToken,
    };
  } catch (error) {
    console.error('❌ Firebase email/password authentication error:', error);

    let errorMessage = 'Authentication failed';
    if (error instanceof Error) {
      if (error.message.includes('auth/user-not-found')) {
        errorMessage = 'No account found with this email address';
      } else if (error.message.includes('auth/wrong-password')) {
        errorMessage = 'Incorrect password';
      } else if (error.message.includes('auth/invalid-email')) {
        errorMessage = 'Invalid email address';
      } else if (error.message.includes('auth/user-disabled')) {
        errorMessage = 'This account has been disabled';
      } else if (error.message.includes('auth/invalid-credential')) {
        errorMessage = 'Invalid email or password';
      } else {
        errorMessage = error.message;
      }
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get the current Firebase user
 */
export const getCurrentFirebaseUser = (): User | null => {
  if (!auth) {
    return null;
  }
  return auth.currentUser;
};

/**
 * Sign out from Firebase Auth
 */
export const signOut = async (): Promise<void> => {
  if (!auth) {
    console.warn('⚠️ Firebase not initialized - cannot sign out');
    return;
  }

  try {
    await auth.signOut();
    console.log('✅ Signed out from Firebase successfully');
  } catch (error) {
    console.error('❌ Firebase sign out error:', error);
    throw error;
  }
};

/**
 * Get the Firebase ID token for backend authentication
 */
export const getFirebaseIdToken = async (): Promise<string | null> => {
  if (!auth) {
    console.error('❌ Firebase not initialized');
    return null;
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('❌ No authenticated Firebase user found');
      return null;
    }

    const token = await user.getIdToken();
    console.log('🔑 Firebase ID token obtained');
    return token;
  } catch (error) {
    console.error('❌ Error getting Firebase ID token:', error);
    return null;
  }
};

/**
 * Extract user information from Firebase User object
 */
export const extractFirebaseUserInfo = (user: User): FirebaseUserInfo => {
  return {
    email: user.email || '',
    name: user.displayName || user.email?.split('@')[0] || '',
    displayName: user.displayName || user.email?.split('@')[0] || '',
    uid: user.uid,
    emailVerified: user.emailVerified,
  };
};

/**
 * Validate Firebase ID token format
 */
export const validateFirebaseIdToken = (token: string): boolean => {
  try {
    // Firebase ID tokens are JWT tokens with 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('❌ Invalid Firebase ID token format - not a JWT');
      return false;
    }

    // Decode the header to check if it's a Firebase token
    const header = JSON.parse(atob(parts[0]));
    if (header.alg !== 'RS256') {
      console.error('❌ Invalid Firebase ID token - wrong algorithm');
      return false;
    }

    console.log('✅ Firebase ID token format is valid');
    return true;
  } catch (error) {
    console.error('❌ Error validating Firebase ID token:', error);
    return false;
  }
};
